<template>
  <b-modal
    :id="id"
    size="lg"
    :ok-title="trans(!!product ? 'save' : 'create')"
    :ok-disabled="!canSave"
    :cancel-title="trans('cancel')"
    @shown="onShown"
    @hidden="onHidden"
    @ok="onSave"
  >
    <template #modal-title>
      <b-row>
        <b-col>
          {{ trans('product') }}
          <span v-if="!!product">: {{ product.name }}</span>
          <small v-if="!!product" class="text-muted ml-2">
            #{{ product.id }}
          </small>
        </b-col>
      </b-row>
    </template>

    <b-row class="mb-3">
      <b-col>
        <b-form-group>
          <label>
            {{ trans('name') }}
            <small class="text-muted">({{ trans('title').toLowerCase() }})</small> *
          </label>
          <b-form-input v-model="name" :state="nameState" :disabled="busy" />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group>
          <label>
            {{ trans('tags') }} ({{ trans('categories').toLowerCase() }})
          </label>
          <model-treeselect
            v-if="!!product && !!tag"
            :organization="organization"
            :models="allTags"
            id-attribute="name"
            v-model="tags"
            :clearable="false"
          ></model-treeselect>
          <b-form-text v-if="visible && !!product && !!tag && !pageTagPresent">
            <span class="text-danger">
              <i class="fa fa-fw fa-exclamation-triangle"></i>
              {{ trans('microsite_products_page_tag_removed_warning').replace(':tag', tag) }}
            </span>
          </b-form-text>
          <model-treeselect
            v-if="!product && !!tag"
            :organization="organization"
            :models="allTags"
            :multiple="false"
            :clearable="false"
            id-attribute="name"
            v-model="tag"
            disabled
          ></model-treeselect>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col>
        <b-form-group :label="trans('displayed_price') + ' ($) *'">
          <b-form-input type="number" min="0" step="0.01" v-model="price" :state="priceState" :disabled="busy" />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="trans('taxes')">
          <model-treeselect
            :organization="organization"
            :models="allTaxes"
            v-model="tax_ids"
            :placeholder="trans('taxes')"
            :label-attribute="(model) => {return model.description || model.name}"
            :disabled="busy"
          ></model-treeselect>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mb-2">
      <b-col>
        <b-form-group>
          <label>
            {{ trans('short_description') }}
            <small class="text-muted">({{ trans('subtitle').toLowerCase() }})</small>
          </label>
          <b-form-input v-model="short_description" :disabled="busy" />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="SKU">
          <b-form-input v-model="sku" :disabled="busy" />
        </b-form-group>
      </b-col>
    </b-row>
    
    <b-form-group class="mb-3">
      <label>
        {{ trans('description') }}
        <b-button
          v-if="!description"
          variant="outline-secondary"
          size="sm"
          class="py-0 ml-2"
          @click="show_description = !show_description"
        >
          <small>
            <i :class="`fa fa-fw fa-eye${show_description ? '-slash' : ''}`"></i>
            {{ trans(show_description ? 'hide' : 'show') }}
          </small>
        </b-button>
      </label>
      <html-textarea
        v-if="show_description || !!description"
        :organization="organization"
        v-model="description"
      ></html-textarea>
    </b-form-group>

    <b-row class="mb-3">
      <b-col>
        <b-form-group :label="trans('accept_a_coupon')">
          <b-form-checkbox v-model="accept_coupon" switch>
            {{ trans(accept_coupon ? 'yes' : 'no') }}
          </b-form-checkbox>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group v-if="accept_coupon" :label="trans('preapply_promo_code')">
          <promo-code-dropdown
            :organization="organization"
            type="promo_code"
            size="sm"
            icon="tag"
            :null-label="trans('none')"
            :placeholder="trans('none')"
            v-model="promo_code_id"
            @promo-code="promo_code = $event"
            :disabled="busy"
          ></promo-code-dropdown>
        </b-form-group>
        <b-form-group class="mt-3" v-if="promo_code_id">
          <b-form-checkbox switch v-model="display_promo_dollar" :disabled="busy">
            {{ trans('display_promo_dollar') }}
          </b-form-checkbox>
        </b-form-group>
      </b-col>
    </b-row>

    <p>{{ trans('package_up_to_3_images') }}</p>
    <b-form-group :label="trans('image')+' 1'">
      <image-tab
        modal-postfix="product1"
        :organization="organization"
        type="hero"
        :image_url="image_url"
        @image_url="image_url = $event"
        only-image
        show-clear
        return-compressed-image
      ></image-tab>
    </b-form-group>
    <b-form-group :label="trans('image')+' 2'">
      <image-tab
        modal-postfix="product2"
        :organization="organization"
        type="hero"
        :image_url="image_url2"
        @image_url="image_url2 = $event"
        only-image
        show-clear
        return-compressed-image
      ></image-tab>
    </b-form-group>
    <b-form-group :label="trans('image')+' 3'">
      <image-tab
        modal-postfix="product3"
        :organization="organization"
        type="hero"
        :image_url="image_url3"
        @image_url="image_url3 = $event"
        only-image
        show-clear
        return-compressed-image
      ></image-tab>
    </b-form-group>

    <p v-if="busy">
      <i class="fa fa-fw fa-circle-notch fa-spin"></i>
      {{ trans('please_wait') }}
    </p>
    <p v-if="error" class="text-danger">
      {{ error }}
    </p>
  </b-modal>
</template>

<script>
  import ImageTab from './../template/BlockImageTabComponent'
  import HtmlTextarea from './../../../common/components/HtmlTextarea'
  import PromoCodeDropdown from './../promo-code/PromoCodeDropdown'
  import ModelTreeselect from './../../../common/components/ModelTreeselect'

  export default {
    props: {
      id: {
        type: String,
        default: 'product-quick-edit-modal',
      },
      organization: Object,
      product: Object,
      block: Object, // Will be null when main products page
      tag: String, // Tag name of the current page
      allTags: Array, // All tags visible in this online store
      allTaxes: Array,
    },
    components: {
      ImageTab,
      HtmlTextarea,
      PromoCodeDropdown,
      ModelTreeselect,
    },
    data() {
      return {
        trans: trans,
        lang: lang,
        busy: false,
        error: null,
        visible: false,
        name: null,
        short_description: null,
        sku: null,
        description: null,
        show_description: false,
        image_url: null,
        image_url2: null,
        image_url3: null,
        price: null,
        accept_coupon: false,
        promo_code_id: null,
        display_promo_dollar: false,
        tags: [], // Tag names (not tag objects)
        tax_ids: [],
      }
    },
    computed: {
      taxes() {
        return _.filter(this.allTaxes || [], tax => {
          return this.tax_ids.indexOf(tax.id) >= 0
        })
      },
      serialized() {
        let serialized =  {
          name: this.name,
          price: this.price,
          taxes: this.taxes,
          short_description: this.short_description,
          sku: this.sku,
          description: this.description,
          image_url: this.image_url,
          image_url2: this.image_url2,
          image_url3: this.image_url3,
          accept_coupon: this.accept_coupon,
          promo_code_id: this.promo_code_id,
          display_promo_dollar: this.display_promo_dollar,
        }
        if (!!this.product) {
          serialized.tags = _.clone(this.tags)
        } else {
          serialized.tag = this.tag
        }
        if (!!this.block) serialized.block_id = this.block.id
        return serialized
      },
      nameState() {
        if (!this.name || !this.name.trim()) return false
        return true
      },
      priceState() {
        if (!_.isNumeric(this.price) || isNaN(this.price) || parseFloat(this.price) < 0) return false
        return true
      },
      pageTagPresent() {
        return this.tags.indexOf(this.tag) >= 0
      },
      canSave() {
        return this.nameState && this.priceState
      },
    },
    methods: {
      onSave(e) {
        e.preventDefault()
        this.busy = true
        this.error = null

        let url = !!this.product ? 
          `/organization/${this.organization.id}/product/${this.product.id}/quick_update` :
          `/organization/${this.organization.id}/product/quick_create`
        let method = !!this.product ? 'patch' : 'post'
        axios[method](url, this.serialized)
        .then(response => {
          if (!this.product) {
            window.location.reload()
            return
          }
          this.busy = false
          this.$emit('saved', response.data)
          this.$bvModal.hide(this.id)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onShown() {
        if (this.product) {
          this.name = this.product.name
          this.price = this.product.price
          this.tax_ids = _.map(this.product.taxes, 'id')
          this.short_description = this.product.short_description
          this.sku = this.product.sku
          this.description = this.product.description
          this.show_description = !!this.description
          this.image_url = this.product.image_url
          this.image_url2 = this.product.image_url2
          this.image_url3 = this.product.image_url3
          this.accept_coupon = this.product.accept_coupon
          this.promo_code_id = this.product.promo_code_id
          this.display_promo_dollar = this.product.display_promo_dollar
          this.tags = _.map(this.product.tags, 'name')
        }
        else {
          this.name = null
          this.price = null
          this.tax_ids = []
          this.short_description = null
          this.sku = null
          this.description = null
          this.show_description = false
          this.image_url = null
          this.image_url2 = null
          this.image_url3 = null
          this.accept_coupon = false
          this.promo_code_id = null
          this.display_promo_dollar = false
          this.tags = []
        }
        this.visible = true
      },
      onHidden() {
        this.busy = false
        this.error = null
        this.visible = false
      },
    },
    mounted() {
      window[_.camelCase(this.id)] = this
    },
  }
</script>